import { Observable, interval } from 'rxjs';
import { switchMap, map, filter, take, timeout as rxTimeout } from 'rxjs/operators';
import { SSEParser, SSEMessage } from './sse-parser';

export interface SSEClientConfig<T> {
  url: string;
  headers?: Record<string, string>;
  timeout?: number;
  pollingInterval?: number;
  pollingUrl?: string;
  shouldPoll?: (error: any) => boolean;
  parseMessage: (message: SSEMessage) => T | null;
  isTerminal: (data: T) => boolean;
  isError: (data: T) => boolean;
  getErrorMessage?: (data: T) => string;
}

export class SSEClient<T> {
  private abortController?: AbortController;

  constructor(private config: SSEClientConfig<T>) {}

  connect(): Observable<T> {
    const source$ = new Observable<T>((observer) => {
      this.abortController = new AbortController();
      let hasEmitted = false;

      const connectSSE = async (): Promise<void> => {
        try {
          const response = await fetch(this.config.url, {
            headers: {
              Accept: 'text/event-stream',
              ...this.config.headers,
            },
            signal: this.abortController!.signal,
          });

          if (!response.ok) {
            throw new Error(`SSE connection failed: ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('No readable stream available');
          }

          const decoder = new TextDecoder();
          const parser = new SSEParser();
          let terminalData: T | null = null;

          while (!this.abortController!.signal.aborted && !terminalData) {
            const { value, done } = await reader.read();

            if (done) {
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const messages = parser.parse(chunk);

            for (const message of messages) {
              const data = this.config.parseMessage(message);
              if (!data) {
                continue;
              }

              if (this.config.isTerminal(data)) {
                hasEmitted = true;
                terminalData = data;
                break;
              }
            }
          }

          if (terminalData) {
            if (this.config.isError(terminalData)) {
              const errorMsg = this.config.getErrorMessage?.(terminalData) || 'Operation failed';
              observer.error(new Error(errorMsg));
            } else {
              observer.next(terminalData);
              observer.complete();
            }
          }
        } catch (error) {
          if (this.abortController!.signal.aborted && hasEmitted) {
            return;
          }

          if (this.config.shouldPoll?.(error) && this.config.pollingUrl) {
            this.startPolling().subscribe({
              next: (result) => observer.next(result),
              error: (err) => observer.error(err),
              complete: () => observer.complete(),
            });
          } else {
            observer.error(error);
          }
        }
      };

      connectSSE();

      return (): void => {
        this.abortController?.abort();
      };
    });

    return this.config.timeout !== null && this.config.timeout !== undefined
      ? source$.pipe(rxTimeout(this.config.timeout))
      : source$;
  }

  private startPolling(): Observable<T> {
    if (!this.config.pollingUrl) {
      throw new Error('Polling URL not configured');
    }

    const polling$ = interval(this.config.pollingInterval || 2000).pipe(
      switchMap(() =>
        fetch(this.config.pollingUrl!, {
          headers: this.config.headers,
        }).then((res) => res.json())
      ),
      map((response) => {
        const data = response as T;
        if (this.config.isTerminal(data)) {
          return { data, done: true };
        }
        return { data, done: false };
      }),
      filter((result) => result.done),
      map((result) => {
        if (this.config.isError(result.data)) {
          const errorMsg = this.config.getErrorMessage?.(result.data) || 'Operation failed';
          throw new Error(errorMsg);
        }
        return result.data;
      }),
      take(1),
    );

    return this.config.timeout !== null && this.config.timeout !== undefined
      ? polling$.pipe(rxTimeout(this.config.timeout))
      : polling$;
  }

  disconnect(): void {
    this.abortController?.abort();
  }
}
